import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await emailjs.send('service_39q4tiy', 'template_iqnf6tf', {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        message: form.message,
        to_name: 'Astrology Times'
      }, 'b1OhwAzL-ZnisRbry');
      setSent(true);
    } catch (err) {
      console.error('Email error:', err);
      alert(isHindi ? 'संदेश भेजने में त्रुटि' : 'Error sending message. Please try WhatsApp instead.');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16"
      style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1770910670915-2016cede7806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxjb25zdGVsbGF0aW9uJTIwZGFyayUyMG5pZ2h0JTIwc2t5fGVufDB8fHx8MTc3NjI3MzQ2NXww&ixlib=rb-4.1.0&q=85)', backgroundSize: 'cover', backgroundAttachment: 'fixed' }}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F2EB] text-center mb-4" data-testid="contact-heading">
          {isHindi ? 'संपर्क करें' : 'Contact Us'}
        </h1>
        <p className="text-[#A3A3A3] text-center mb-12">
          {isHindi ? 'हमसे संपर्क करने में संकोच न करें' : 'Feel free to reach out to us'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'संपर्क जानकारी' : 'Contact Information'}</h3>
              <div className="space-y-4">
                <a href="https://wa.me/918099975662" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors" data-testid="whatsapp-link-1">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  <span>8099975662 (WhatsApp)</span>
                </a>
                <a href="https://wa.me/919440698250" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors" data-testid="whatsapp-link-2">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  <span>9440698250 (WhatsApp)</span>
                </a>
                <a href="mailto:astrologytimes5@gmail.com"
                  className="flex items-center gap-3 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors">
                  <Mail className="w-5 h-5 text-[#C9A84C]" />
                  <span>astrologytimes5@gmail.com</span>
                </a>
                <div className="flex items-center gap-3 text-[#F5F2EB]">
                  <MapPin className="w-5 h-5 text-[#C9A84C]" />
                  <span>Hyderabad, Telangana, India</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'कार्य समय' : 'Working Hours'}</h3>
              <div className="space-y-2 text-[#A3A3A3] text-sm">
                <p>{isHindi ? 'सोमवार - शनिवार' : 'Monday - Saturday'}: 11am-1pm, 3pm-7pm</p>
                <p>{isHindi ? 'रविवार' : 'Sunday'}: 11am-3pm</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6">
            {sent ? (
              <div className="text-center py-8">
                <Send className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
                <h3 className="text-2xl font-serif text-[#F5F2EB] mb-2">{isHindi ? 'संदेश भेजा गया!' : 'Message Sent!'}</h3>
                <p className="text-[#A3A3A3]">{isHindi ? 'हम जल्द ही उत्तर देंगे' : 'We will reply soon'}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <h3 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'संदेश भेजें' : 'Send Message'}</h3>
                <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'नाम' : 'Name'}</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                <div><label className="text-xs text-[#A3A3A3] uppercase">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'फोन' : 'Phone'}</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
                <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'संदेश' : 'Message'}</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                    className="w-full bg-transparent border border-[#C9A84C]/30 p-2 mt-1 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                <button type="submit" disabled={sending}
                  className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] disabled:opacity-40 uppercase tracking-wider text-sm"
                  data-testid="contact-submit-btn">
                  {sending ? 'Sending...' : (isHindi ? 'संदेश भेजें' : 'Send Message')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
